import React, { useCallback } from 'react';
import { numberInputComma } from 'utils/snippet';
import { ChatOrderFormStyle } from './ChatOrderFormStyle';

const MAX_PRICE = 100_000_000;

interface ChatOrderFormProps {
  productName: string;
  price: string;
  isSubmitting: boolean;
  productChips: string[];
  onProductNameChange: (value: string) => void;
  onPriceChange: (value: string) => void;
  onSubmit: () => void;
}

const ChatOrderForm = ({
  productName,
  price,
  isSubmitting,
  productChips,
  onProductNameChange,
  onPriceChange,
  onSubmit,
}: ChatOrderFormProps) => {
  const handleChipClick = useCallback(
    (chip: string) => {
      onProductNameChange(chip);
    },
    [onProductNameChange]
  );

  const handleProductNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onProductNameChange(e.target.value);
    },
    [onProductNameChange]
  );

  const handlePriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, '');
      if (parseInt(value, 10) > MAX_PRICE) {
        return;
      }
      onPriceChange(value);
    },
    [onPriceChange]
  );

  const isSubmitDisabled = isSubmitting || !productName.trim() || !price;

  return (
    <ChatOrderFormStyle>
      <div className="input-section">
        <div className="input-label">상품명 입력</div>
        <div className="example">예) 배추 10kg, 감귤 2.5kg</div>
        <input
          type="text"
          className="input-field"
          placeholder="예) 배추 10kg, 감귤 2.5kg"
          value={productName}
          onChange={handleProductNameChange}
        />
        <div className="chip-list">
          {productChips.map((chip) => (
            <button
              key={chip}
              type="button"
              className={`chip ${productName === chip ? 'selected' : ''}`}
              onClick={() => handleChipClick(chip)}
            >
              {chip}
            </button>
          ))}
        </div>
      </div>

      <div className="input-section">
        <div className="input-label">얼마를 요청 할까요?</div>
        <div className="price-input-wrapper">
          <input
            type="text"
            className="input-field"
            placeholder="가격을 입력해 주세요"
            value={price ? numberInputComma(parseInt(price, 10)) : ''}
            onChange={handlePriceChange}
          />
          {price && <div className="price-korean">{convertToKoreanWon(parseInt(price, 10))}</div>}
        </div>
      </div>

      <button
        type="button"
        className="submit-button"
        onClick={onSubmit}
        disabled={isSubmitDisabled}
      >
        {isSubmitting ? '요청 중...' : '결제 요청하기'}
      </button>
    </ChatOrderFormStyle>
  );
};

export default ChatOrderForm;

const formatNumberWithComma = (num: number): string => {
  return num.toLocaleString('ko-KR');
};

const convertToKoreanWon = (amount: number): string => {
  if (amount <= 0) return '';
  if (amount > MAX_PRICE) return '1억원 초과';

  const result: string[] = [];

  // 억 단위
  const eok = Math.floor(amount / 100_000_000);
  if (eok > 0) {
    result.push(`${eok}억`);
  }

  // 만 단위
  const man = Math.floor((amount % 100_000_000) / 10_000);
  if (man > 0) {
    result.push(`${formatNumberWithComma(man)}만`);
  }

  // 원 단위
  const won = amount % 10_000;
  if (won > 0) {
    result.push(`${formatNumberWithComma(won)}`);
  }

  return result.join(' ') + '원';
};